/**
 * Supported cities configuration for location filtering.
 * This serves as the single source of truth for all city-related data including
 * identifiers, display names, center coordinates, bounds, and polygon coordinates
 * for geographic filtering.
 */

export type CityBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

export type CityCenter = {
  lat: number;
  lng: number;
};

/**
 * Helper to derive bounds from a rectangular polygon.
 * Polygon points are [lat, lng] in order: NW, NE, SE, SW
 */
function boundsFromPolygon(polygon: [number, number][]): CityBounds {
  const lats = polygon.map((p) => p[0]);
  const lngs = polygon.map((p) => p[1]);
  return {
    north: Math.max(...lats),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    west: Math.min(...lngs)
  };
}

/**
 * Helper to derive center from bounds.
 */
function centerFromBounds(bounds: CityBounds): CityCenter {
  return {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2
  };
}

export type CityConfig = {
  key: string;
  displayName: string;
  shortName: string;
  image: string;
  polygon: [number, number][];
  bounds: CityBounds;
  center: CityCenter;
};

/**
 * Helper to create a city config with computed bounds and center.
 */
function createCityConfig(config: {
  key: string;
  displayName: string;
  shortName: string;
  image: string;
  polygon: [number, number][];
}): CityConfig {
  const bounds = boundsFromPolygon(config.polygon);
  const center = centerFromBounds(bounds);
  return {
    ...config,
    bounds,
    center
  };
}

const MIAMI = createCityConfig({
  key: 'miami',
  displayName: 'Miami, Florida',
  shortName: 'Miami, FL',
  image: '/images/miami.jpg',
  polygon: [
    [25.9742, -80.4389], // NW - Miami-Dade area
    [25.9742, -80.1389], // NE
    [25.6742, -80.1389], // SE
    [25.6742, -80.4389] // SW
  ]
});

const HOUSTON = createCityConfig({
  key: 'houston',
  displayName: 'Houston, Texas',
  shortName: 'Houston, TX',
  image: '/images/houston.jpg',
  polygon: [
    [30.0694, -95.8244], // NW - Greater Houston area
    [30.0694, -94.8244], // NE
    [29.5694, -94.8244], // SE
    [29.5694, -95.8244] // SW
  ]
});

const LOS_ANGELES = createCityConfig({
  key: 'los_angeles',
  displayName: 'Los Angeles, California',
  shortName: 'Los Angeles, CA',
  image: '/images/los-angeles.jpg',
  polygon: [
    [34.3373, -118.6681], // NW - LA metro area
    [34.3373, -117.6681], // NE
    [33.7037, -117.6681], // SE
    [33.7037, -118.6681] // SW
  ]
});

const CHICAGO = createCityConfig({
  key: 'chicago',
  displayName: 'Chicago, Illinois',
  shortName: 'Chicago, IL',
  image: '/images/chicago.jpg',
  polygon: [
    [42.0231, -87.9401], // NW - Chicago metro area
    [42.0231, -87.5244], // NE
    [41.6445, -87.5244], // SE
    [41.6445, -87.9401] // SW
  ]
});

export const SUPPORTED_CITIES = {
  MIAMI,
  HOUSTON,
  LOS_ANGELES,
  CHICAGO
} as const;

/**
 * Type representing a supported city configuration.
 */
export type SupportedCityConfig =
  (typeof SUPPORTED_CITIES)[keyof typeof SUPPORTED_CITIES];

/**
 * Array of featured cities for display in the UI.
 */
export const FEATURED_CITIES: CityConfig[] = [
  SUPPORTED_CITIES.LOS_ANGELES,
  SUPPORTED_CITIES.HOUSTON,
  SUPPORTED_CITIES.MIAMI,
  SUPPORTED_CITIES.CHICAGO
];

/**
 * Default center location (Miami).
 */
export const DEFAULT_CENTER: CityCenter = SUPPORTED_CITIES.MIAMI.center;

/**
 * Union type of all supported city keys.
 */
export type SupportedCityKey = SupportedCityConfig['key'];

/**
 * Gets all supported city keys.
 */
export function getSupportedCityKeys(): SupportedCityKey[] {
  return Object.values(SUPPORTED_CITIES).map((city) => city.key);
}

/**
 * Gets city configuration by key.
 */
export function getCityConfig(key: string): SupportedCityConfig | undefined {
  return Object.values(SUPPORTED_CITIES).find((city) => city.key === key);
}

/**
 * Checks if a city key is supported.
 */
export function isSupportedCityKey(key: string): key is SupportedCityKey {
  return getSupportedCityKeys().includes(key as SupportedCityKey);
}
